// app.module.ts
import { CommonModule } from "@angular/common";
import { provideHttpClient, withInterceptorsFromDi, withXhr } from "@angular/common/http";
import {
	CUSTOM_ELEMENTS_SCHEMA,
	EnvironmentInjector,
	InjectionToken,
	NO_ERRORS_SCHEMA,
	NgModule,
	createEnvironmentInjector,
	inject,
} from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";

import { ServiceWorkerModule } from "@angular/service-worker";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { CbtaWebModule } from "./cbta-web/cbta-web.module";

import { ErrorComponent } from "./error/error.component";
import { LoginComponent } from "./login/login.component";
import { NotFoundComponent } from "./not-found/not-found.component";
import { SpinnerComponent } from "./spinner/spinner.component";

import { provideAppInitializer } from "@angular/core";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { DBConfig, NgxIndexedDBService, provideIndexedDb } from "ngx-indexed-db";
import { environment } from "src/environments/environment";
import { CONSTANT } from "./constant";
import { CustomSwRegistrarService } from "./core/custom-sw-registrar.service";
import { AppUpdateService } from "./core/pwa/app-update.service";

// ✅ Token for eTrainingDB
export const ETRAINING_DB = new InjectionToken<NgxIndexedDBService>("ETRAINING_DB");

// --------------------
// Migration logic (original for cbtaDB)
// --------------------
const migrationFactory = () => {
	return {
		1: (db: IDBDatabase, transaction: IDBTransaction) => {
			// Initial setup if needed
		},
		4: (db: IDBDatabase, transaction: IDBTransaction) => {
			// FORM migration
			if (transaction && transaction.objectStore(CONSTANT.INDEXEDDB.FORM)) {
				const oldFormStore = transaction.objectStore(CONSTANT.INDEXEDDB.FORM);
				let newFormStore: IDBObjectStore;
				if (!db.objectStoreNames.contains(CONSTANT.INDEXEDDB.FORM)) {
					newFormStore = db.createObjectStore(CONSTANT.INDEXEDDB.FORM, {
						keyPath: "id",
						autoIncrement: false,
					});
					newFormStore.createIndex("id", "id", { unique: true });
					newFormStore.createIndex("data", "data", { unique: false });
					newFormStore.createIndex("stringify", "stringify", { unique: false });
				} else {
					newFormStore = transaction.objectStore(CONSTANT.INDEXEDDB.FORM);
				}

				oldFormStore.openCursor().onsuccess = (event) => {
					const cursor = (event.target as IDBRequest).result;
					if (cursor) {
						const newData = { ...cursor.value };
						newFormStore.put(newData);
						cursor.continue();
					}
				};
			}

			// DRAFT migration
			if (transaction && transaction.objectStore(CONSTANT.INDEXEDDB.DRAFT)) {
				const oldDraftStore = transaction.objectStore(CONSTANT.INDEXEDDB.DRAFT);
				let newDraftStore: IDBObjectStore;
				if (!db.objectStoreNames.contains(CONSTANT.INDEXEDDB.DRAFT)) {
					newDraftStore = db.createObjectStore(CONSTANT.INDEXEDDB.DRAFT, {
						keyPath: "id",
						autoIncrement: false,
					});
					newDraftStore.createIndex("id", "id", { unique: true });
					newDraftStore.createIndex("formDetails", "formDetails", { unique: false });
					newDraftStore.createIndex("assessmentDate", "assessmentDate", { unique: false });
					newDraftStore.createIndex("trainingInformation", "trainingInformation", { unique: false });
					newDraftStore.createIndex("candidateRating", "candidateRating", { unique: false });
					newDraftStore.createIndex("signOffDetails", "signOffDetails", { unique: false });
					newDraftStore.createIndex("continuePage", "continuePage", { unique: false });
					newDraftStore.createIndex("toBeSynced", "toBeSynced", { unique: false });
					newDraftStore.createIndex("synced", "synced", { unique: false });
				} else {
					newDraftStore = transaction.objectStore(CONSTANT.INDEXEDDB.DRAFT);
				}

				oldDraftStore.openCursor().onsuccess = (event) => {
					const cursor = (event.target as IDBRequest).result;
					if (cursor) {
						const newData = { ...cursor.value };
						newDraftStore.put(newData);
						cursor.continue();
					}
				};
			}
		},
	};
};

// --------------------
// cbtaDB config (default, with migration)
// --------------------
const cbtaDbConfig: DBConfig = {
	name: "cbtaDB",
	version: 4,
	objectStoresMeta: [
		{
			store: CONSTANT.INDEXEDDB.FORM,
			storeConfig: { keyPath: "id", autoIncrement: false },
			storeSchema: [
				{ name: "id", keypath: "id", options: { unique: true } },
				{ name: "data", keypath: "data", options: { unique: false } },
				{ name: "stringify", keypath: "stringify", options: { unique: false } },
			],
		},
		{
			store: CONSTANT.INDEXEDDB.DRAFT,
			storeConfig: { keyPath: "id", autoIncrement: false },
			storeSchema: [
				{ name: "id", keypath: "id", options: { unique: true } },
				{ name: "formDetails", keypath: "formDetails", options: { unique: false } },
				{ name: "assessmentDate", keypath: "assessmentDate", options: { unique: false } },
				{ name: "trainingInformation", keypath: "trainingInformation", options: { unique: false } },
				{ name: "candidateRating", keypath: "candidateRating", options: { unique: false } },
				{ name: "signOffDetails", keypath: "signOffDetails", options: { unique: false } },
				{ name: "continuePage", keypath: "continuePage", options: { unique: false } },
				{ name: "toBeSynced", keypath: "toBeSynced", options: { unique: false } },
				{ name: "updateOBComments", keypath: "updateOBComments", options: { unique: false } },
				{ name: "synced", keypath: "synced", options: { unique: false } },
			],
		},
	],
	migrationFactory,
};

// --------------------
// eTrainingDB config (secondary, no migration yet)
// --------------------
const eTrainingDbConfig: DBConfig = {
	name: "eTrainingDB",
	version: 1,
	objectStoresMeta: [
		{
			store: CONSTANT.INDEXEDDB.FORM,
			storeConfig: { keyPath: "id", autoIncrement: false },
			storeSchema: [
				{ name: "id", keypath: "id", options: { unique: true } },
				{ name: "data", keypath: "data", options: { unique: false } },
				{ name: "stringify", keypath: "stringify", options: { unique: false } },
			],
		},
		{
			store: CONSTANT.INDEXEDDB.DRAFT,
			storeConfig: { keyPath: "id", autoIncrement: false },
			storeSchema: [
				{ name: "id", keypath: "id", options: { unique: true } },
				{ name: "formDetails", keypath: "formDetails", options: { unique: false } },
				{ name: "assessmentDate", keypath: "assessmentDate", options: { unique: false } },
				{ name: "trainingInformation", keypath: "trainingInformation", options: { unique: false } },
				{ name: "candidateRating", keypath: "candidateRating", options: { unique: false } },
				{ name: "signOffDetails", keypath: "signOffDetails", options: { unique: false } },
				{ name: "continuePage", keypath: "continuePage", options: { unique: false } },
				{ name: "toBeSynced", keypath: "toBeSynced", options: { unique: false } },
				{ name: "updateOBComments", keypath: "updateOBComments", options: { unique: false } },
				{ name: "synced", keypath: "synced", options: { unique: false } },
			],
		},
		{
			store: CONSTANT.INDEXEDDB.AUTH,
			storeConfig: { keyPath: "key", autoIncrement: false },
			storeSchema: [
				{ name: "key", keypath: "key", options: { unique: true } },
				{ name: "token", keypath: "token", options: { unique: false } },
			],
		},
	],
};

@NgModule({
	declarations: [AppComponent, ErrorComponent, LoginComponent, NotFoundComponent, SpinnerComponent],
	imports: [
		CommonModule,
		BrowserModule,
		AppRoutingModule,
		CbtaWebModule,
		MatSnackBarModule,

		ServiceWorkerModule.register("custom-sw.js", {
			enabled: ["dev", "stg", "prod"].includes(environment.env) || false,
			registrationStrategy: "registerImmediately",
			updateViaCache: "none",
		}),
	],
	providers: [
		// Animations support - Required for Angular Material components
		// Note: provideAnimations() is deprecated but still necessary until Material migrates to native CSS animations
		// provideAnimations(),
		provideAnimationsAsync(),

		// HttpClient with interceptor support
		provideHttpClient(withXhr(), withInterceptorsFromDi()),

		// App initializer for custom service worker
		provideAppInitializer(() => {
			const customSwRegistrar = inject(CustomSwRegistrarService);
			return customSwRegistrar.register();
		}),

		// 1) Primary DB at root
		provideIndexedDb(cbtaDbConfig),

		// 2) Secondary DB via token, hosted in a CHILD injector
		{
			provide: ETRAINING_DB,
			deps: [EnvironmentInjector],
			useFactory: (rootEnv: EnvironmentInjector) => {
				const child = createEnvironmentInjector([provideIndexedDb(eTrainingDbConfig)], rootEnv);
				return child.get(NgxIndexedDBService);
			},
		},
	],
	schemas: [NO_ERRORS_SCHEMA, CUSTOM_ELEMENTS_SCHEMA],
	bootstrap: [AppComponent],
})
export class AppModule {
	constructor(_appUpdate: AppUpdateService) {}
}
